// src/components/ChineseLookup.js
import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import './KanjiLookup.css'; // Reuse styling
// import chineseCharData from '../Languages/Chinese/???'; // TODO: Need Hanzi character data source
import apiClient, { API_BASE_URL } from '../utils/apiClient';
import logger from '../utils/logger';
import { ReactComponent as SpeakerIcon } from '../images/icons/speaker.svg';
import { useWordContext } from '../context/WordContext';

// ADDED baseLanguage to destructured props
const ChineseLookup = ({ word, children, sentence, baseLanguage }) => {
    const [showPopup, setShowPopup] = useState(false);
    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
    const [data, setData] = useState(null); // For word definition data
    const [contextData, setContextData] = useState(null); // For contextual info
    const [grammarData, setGrammarData] = useState(null); // For grammar info
    const [hanziDetailsData, setHanziDetailsData] = useState(null); // For individual character details
    const [loading, setLoading] = useState(false);
    const [contextLoading, setContextLoading] = useState(false);
    const [grammarLoading, setGrammarLoading] = useState(false);
    const [error, setError] = useState(null);
    const [contextError, setContextError] = useState(null);
    const [grammarError, setGrammarError] = useState(null);
    const [openAccordionIndex, setOpenAccordionIndex] = useState(null);
    const [openAccordion, setOpenAccordion] = useState(null); // Top-level accordion (context, grammar)
    const [wordGrade, setWordGrade] = useState(1);
    const [wordKnown, setWordKnown] = useState(false);
    const [hanziGrades, setHanziGrades] = useState({});
    const [hanziKnownStatus, setHanziKnownStatus] = useState({});
    // Track if word/hanzi was just saved to force UI update
    const [justSavedWord, setJustSavedWord] = useState(false);
    const [justSavedHanzi, setJustSavedHanzi] = useState({}); // Change from string to object to track multiple hanzi
    const [isPlaying, setIsPlaying] = useState(false);
    const cachedAudioUrl = useRef(null);

    // Extract base language from props or fallback to localStorage
    const userBaseLang = baseLanguage || localStorage.getItem('baseLanguage') || 'English';

    logger.debug(`ChineseLookup initialized with word: "${word}", sentence: "${sentence || 'none'}", baseLang: "${userBaseLang}"`);

    // --- WordContext Integration --- Corrected function names
    const {
        isWordSaved,
        isHanziSaved, // Use this for checking Hanzi (corrected from isKanjiSaved)
        addSavedWord,
        removeSavedWord,
        addSavedHanzi, // Use Chinese-specific function
        removeSavedHanzi, // Use Chinese-specific function
        getWordGrade,
        isWordKnown,
        getHanziGrade, // Use Chinese-specific function
        isHanziKnown, // Use Chinese-specific function
        updateWordGrade,
        updateWordKnown,
        updateHanziGrade, // Use Chinese-specific function
        updateHanziKnown, // Use Chinese-specific function
        refreshSavedWords
    } = useWordContext();

    const language = 'Chinese'; // Define language for context calls

    // Saved status for the word
    const isSaved = isWordSaved(word, language) || justSavedWord;
    logger.debug(`ChineseLookup - Word "${word}" saved status:`, isSaved);

    // Extract Hanzi characters from the word
    const hanziChars = useMemo(() =>
        Array.from(word || '').filter((char) => /[\u4E00-\u9FFF]/.test(char))
        , [word]);

    // Get saved status for Hanzi characters - corrected function call
    const savedHanziList = hanziChars.filter(char => isHanziSaved(char) || justSavedHanzi[char]);
    logger.debug(`ChineseLookup - Saved Hanzi in word:`, savedHanziList);

    // Initialize Hanzi grades and known status from context - corrected function calls
    useEffect(() => {
        const newGrades = {};
        const newKnownStatus = {};

        // First, preserve existing grades for all characters
        const existingGrades = { ...hanziGrades };
        const existingKnownStatus = { ...hanziKnownStatus };

        hanziChars.forEach(char => {
            if (isHanziSaved(char) || justSavedHanzi[char]) {
                // If we already have a grade for this character in state, preserve it
                if (existingGrades[char]) {
                    newGrades[char] = existingGrades[char];
                } else {
                    // Otherwise get from context
                    newGrades[char] = getHanziGrade(char);
                }

                // Same for known status
                if (typeof existingKnownStatus[char] === 'boolean') {
                    newKnownStatus[char] = existingKnownStatus[char];
                } else {
                    newKnownStatus[char] = isHanziKnown(char);
                }
            } else {
                newGrades[char] = 1; // Default
                newKnownStatus[char] = false;
            }
        });

        setHanziGrades(newGrades);
        setHanziKnownStatus(newKnownStatus);
        logger.debug('ChineseLookup - Updated Hanzi grades:', newGrades);
    }, [word, isHanziSaved, getHanziGrade, isHanziKnown, justSavedHanzi]);

    // Update local state for word grade/known when status changes in context
    useEffect(() => {
        if (isSaved) {
            setWordGrade(getWordGrade(word, language));
            setWordKnown(isWordKnown(word, language));
        } else {
            setWordGrade(1);
            setWordKnown(false);
        }
    }, [isSaved, word, getWordGrade, isWordKnown]); // Dependencies

    const spanRef = useRef(null);
    const popupRef = useRef(null);

    // Helper to get userId (same as KanjiLookup)
    const getUserId = () => {
        const token = localStorage.getItem('access_token');
        if (token) {
            try {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join(''));
                const decodedToken = JSON.parse(jsonPayload);
                if (decodedToken.sub) {
                    localStorage.setItem('userId', decodedToken.sub);
                    return decodedToken.sub;
                }
            } catch (err) {
                logger.error("ChineseLookup - Error decoding token:", err);
            }
        }
        const localUserId = localStorage.getItem('userId');
        if (!localUserId) {
            logger.warn("ChineseLookup - No userId available");
        }
        return localUserId;
    };

    // --- Data Fetching --- Updated to call endpoints with baseLanguage
    const fetchData = async () => {
        setLoading(true);
        setContextLoading(!!sentence); // Start loading if sentence exists
        setGrammarLoading(!!sentence); // Start loading if sentence exists
        setError(null);
        setContextError(null);
        setGrammarError(null);
        setData(null);
        setHanziDetailsData(null); // Clear old hanzi details
        setContextData(null);
        setGrammarData(null);

        try {
            logger.debug(`ChineseLookup - Fetching data for word: "${word}" in base language: ${userBaseLang}`);

            // Track if we've found a definition
            let definitionFound = false;

            // 1. Try to get definition from Gemini 
            // If sentence is provided, try context-based definition first
            if (sentence) {
                try {
                    logger.debug(`ChineseLookup - Fetching context-based Gemini definition for "${word}" in sentence: "${sentence}"`);

                    // APPEND baseLang
                    const contextResponse = await apiClient.get(`/api/contextDefine?word=${encodeURIComponent(word)}&sentence=${encodeURIComponent(sentence)}&lang=zh&baseLang=${encodeURIComponent(userBaseLang)}`);
                    const contextResult = await contextResponse.json();

                    if (contextResult && contextResult.definitions && contextResult.definitions.length > 0) {
                        // Set data from Gemini response
                        setData({
                            word: word,
                            pinyin: "...", // Will be updated from hanzi details
                            definitions: contextResult.definitions
                        });

                        definitionFound = true;
                        logger.debug("ChineseLookup - Received Gemini context definition:", contextResult);
                    }
                } catch (geminiErr) {
                    logger.error("ChineseLookup - Error fetching from Gemini context definition:", geminiErr);
                    // Will try the regular definition next
                }
            }

            // If no context definition or no sentence, try regular definition
            if (!definitionFound) {
                try {
                    logger.debug(`ChineseLookup - Fetching Gemini definition for "${word}"`);

                    // APPEND baseLang
                    const defineResponse = await apiClient.get(`/api/define?word=${encodeURIComponent(word)}&lang=zh&baseLang=${encodeURIComponent(userBaseLang)}`);
                    const defineResult = await defineResponse.json();

                    if (defineResult && defineResult.definitions && defineResult.definitions.length > 0) {
                        setData({
                            word: word,
                            pinyin: "...", // Will be updated from hanzi details
                            definitions: defineResult.definitions
                        });

                        definitionFound = true;
                        logger.debug("ChineseLookup - Received Gemini definition:", defineResult);
                    }
                } catch (geminiErr) {
                    logger.error("ChineseLookup - Error fetching from Gemini definition:", geminiErr);
                }
            }

            // If still no definition found
            if (!definitionFound) {
                setData({
                    word: word,
                    pinyin: "N/A",
                    definitions: ["Definition not found."]
                });
                logger.debug(`ChineseLookup - No definition found for "${word}"`);
            }

            // --- Fetch Hanzi Details --- 
            if (hanziChars.length > 0) {
                logger.debug(`ChineseLookup - Fetching details for Hanzi: ${hanziChars.join(', ')}`);
                // apiClient.post returns the parsed JSON directly
                // Append baseLanguage to the POST payload if the backend uses it for character translation
                const hanziDataMap = await apiClient.post(`/api/hanzi-details`, {
                    characters: hanziChars,
                    baseLanguage: userBaseLang
                });
                setHanziDetailsData(hanziDataMap);
                logger.debug("ChineseLookup - Hanzi details data received:", hanziDataMap);

                // Update pinyin from hanzi details if available
                if (hanziDataMap && Object.keys(hanziDataMap).length > 0) {
                    // Get the pinyin values for each character
                    const pinyinArray = hanziChars.map(char =>
                        hanziDataMap[char] && hanziDataMap[char].pinyin ? hanziDataMap[char].pinyin : ''
                    ).filter(p => p);

                    // Update state if we have pinyin data
                    if (pinyinArray.length > 0) {
                        setData(prevData => {
                            // Only update if we have existing data
                            if (!prevData) return prevData;

                            return {
                                ...prevData,
                                pinyin: pinyinArray.join(' ')
                            };
                        });
                        logger.debug(`ChineseLookup - Updated pinyin to: ${pinyinArray.join(' ')}`);
                    }
                }
            } else {
                setHanziDetailsData({}); // Set empty object if no hanzi
            }

            // --- Fetch Context/Grammar Notes (if sentence provided) ---
            if (sentence) {
                logger.debug(`ChineseLookup - Fetching context/grammar for word "${word}" in sentence: "${sentence}"`);
                try {
                    // Context Note - Using dedicated Chinese endpoint with baseLang
                    const contextResponse = await apiClient.get(`/api/context-note-chinese?word=${encodeURIComponent(word)}&sentence=${encodeURIComponent(sentence)}&baseLang=${encodeURIComponent(userBaseLang)}`);
                    const contextResult = await contextResponse.json();
                    setContextData({
                        context_note: contextResult.note || "No context note available.",
                        examples: contextResult.examples
                    });
                    logger.debug("ChineseLookup - Context note received:", contextResult.note);
                } catch (ctxErr) {
                    logger.error("ChineseLookup - Error fetching context note:", ctxErr);
                    setContextError(ctxErr.message || "Failed to fetch context");
                    setContextData({ context_note: "Error fetching context." });
                } finally {
                    setContextLoading(false);
                }

                try {
                    // Grammar Note - Using dedicated Chinese endpoint with baseLang
                    const grammarResponse = await apiClient.get(`/api/grammar-note-chinese?word=${encodeURIComponent(word)}&sentence=${encodeURIComponent(sentence)}&baseLang=${encodeURIComponent(userBaseLang)}`);
                    const grammarResult = await grammarResponse.json();
                    setGrammarData({ grammar_note: grammarResult.note || "No grammar note available." });
                    logger.debug("ChineseLookup - Grammar note received:", grammarResult.note);
                } catch (gramErr) {
                    logger.error("ChineseLookup - Error fetching grammar note:", gramErr);
                    setGrammarError(gramErr.message || "Failed to fetch grammar");
                    setGrammarData({ grammar_note: "Error fetching grammar." });
                } finally {
                    setGrammarLoading(false);
                }
            } else {
                // If no sentence, no context/grammar needed
                logger.debug("ChineseLookup - No sentence context available");
                setContextLoading(false);
                setGrammarLoading(false);
            }

        } catch (err) {
            logger.error("ChineseLookup - Error fetching data:", err);
            setError(err.message || "Failed to fetch details");
            // Set fallback data if needed
            if (!data) setData({ word: word, pinyin: "Error", definitions: ["Error fetching definition."] });
            if (!hanziDetailsData) setHanziDetailsData({});
            // If primary fetch fails, ensure context/grammar loading stops if they started
            if (sentence) {
                if (!contextData) setContextData({ context_note: "Error fetching context." });
                if (!grammarData) setGrammarData({ grammar_note: "Error fetching grammar." });
                setContextLoading(false);
                setGrammarLoading(false);
            }
        } finally {
            setLoading(false);
        }
    };

    // --- Event Handlers ---
    const handleClick = () => {
        const newShowPopup = !showPopup;
        setShowPopup(newShowPopup);

        if (spanRef.current) {
            const rect = spanRef.current.getBoundingClientRect();
            const offset = 120;
            setPopupPosition({
                top: rect.top + window.scrollY - offset,
                left: rect.left + window.scrollX,
            });
        }

        if (newShowPopup && !data && !loading && !error) {
            fetchData();
        }
        // Optionally re-fetch context if popup opened without it
        // else if (newShowPopup && !contextData && sentence && !loading) {
        //     fetchData();
        // }
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showPopup && spanRef.current && popupRef.current && !spanRef.current.contains(e.target) && !popupRef.current.contains(e.target)) {
                setShowPopup(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showPopup]);

    const toggleTopAccordion = (section, e) => {
        e.stopPropagation();
        setOpenAccordion(openAccordion === section ? null : section);
    };

    // --- Save/Delete/Grade Handlers (using WordContext) --- Corrected function calls

    // Word handlers
    const handleSaveWord = async (e) => {
        e.stopPropagation();
        
        // Extract definition text and example sentences from fetched data
        let definitionText = '';
        let exampleSentences = [];
        if (data && Array.isArray(data.definitions) && data.definitions.length > 0) {
            definitionText = data.definitions.join('; ');
        }
        if (contextData && Array.isArray(contextData.examples) && contextData.examples.length > 0) {
            exampleSentences = contextData.examples;
        }
        
        if (await addSavedWord(word, language, {
            grade: 1,
            known: false,
            definition: definitionText,
            exampleSentences: exampleSentences
        })) {
            setJustSavedWord(true);
            setWordGrade(1);
            setWordKnown(false);
        } else {
            setError("Failed to save word.");
        }
    };

    const handleDeleteWord = async (e) => {
        e.stopPropagation();
        if (await removeSavedWord(word, language)) {
            setJustSavedWord(false);
        } else {
            setError("Failed to delete word.");
        }
    };

    const handleGradeChange = async (e) => {
        e.stopPropagation();
        const newGrade = parseInt(e.target.value, 10);
        setWordGrade(newGrade);
        if (isSaved) {
            if (!await updateWordGrade(word, language, newGrade)) {
                setError("Failed to update grade.");
            }
        }
    };

    const handleKnownChange = async (e) => {
        e.stopPropagation();
        const isKnown = e.target.checked;
        setWordKnown(isKnown);
        if (isSaved) {
            if (!await updateWordKnown(word, language, isKnown)) {
                setError("Failed to update status.");
            }
        }
    };

    // Hanzi handlers - corrected function calls
    const handleSaveHanzi = async (hanziChar) => {
        const userId = getUserId();

        if (!userId) {
            logger.error("ChineseLookup - No user ID available, cannot save hanzi");
            setError("No user ID available. Please log in again.");
            return;
        }

        try {
            logger.debug(`ChineseLookup - Saving hanzi "${hanziChar}" with userId ${userId}`);
            // Make direct API call to the server
            const result = await apiClient.post('/api/saveUserHanzi', {
                userId,
                hanzi: hanziChar,
                grade: 1,
                known: false,
                type: 'hanzi'
            });

            // Check if result is valid
            if (!result || !result.message) {
                logger.error("ChineseLookup - Failed to save hanzi: Invalid response");
                throw new Error("Failed to save hanzi: Invalid response");
            }

            logger.debug("ChineseLookup - Hanzi saved successfully:", result.message);

            // Set local state to force immediate UI update - update as object to track multiple hanzi
            setJustSavedHanzi(prev => ({ ...prev, [hanziChar]: true }));

            // Only update the grade for this specific hanzi, preserving others
            setHanziGrades(prev => ({ ...prev, [hanziChar]: 1 }));
            setHanziKnownStatus(prev => ({ ...prev, [hanziChar]: false }));

            // Force refresh saved words to update context state
            refreshSavedWords();
        } catch (error) {
            logger.error(`ChineseLookup - Error saving Hanzi ${hanziChar}:`, error);
            setError("Failed to save Hanzi.");
        }
    };

    const handleDeleteHanzi = async (hanziChar) => {
        const userId = getUserId();

        if (!userId) {
            logger.error("ChineseLookup - No user ID available, cannot delete hanzi");
            setError("No user ID available. Please log in again.");
            return;
        }

        try {
            logger.debug(`ChineseLookup - Deleting hanzi "${hanziChar}" with userId ${userId}`);
            // Make direct API call to the server
            const result = await apiClient.post('/deleteUserHanzi', {
                userId,
                hanzi: hanziChar
            });

            // Check if result is valid
            if (!result || !result.message) {
                logger.error("ChineseLookup - Failed to delete hanzi: Invalid response");
                throw new Error("Failed to delete hanzi: Invalid response");
            }

            logger.debug("ChineseLookup - Hanzi deleted successfully:", result.message);

            // Reset saved status for this hanzi - update object property
            setJustSavedHanzi(prev => {
                const updated = { ...prev };
                delete updated[hanziChar];
                return updated;
            });

            // Force refresh saved words to update context state
            refreshSavedWords();
        } catch (error) {
            logger.error(`ChineseLookup - Error deleting Hanzi ${hanziChar}:`, error);
            setError("Failed to delete Hanzi.");
        }
    };

    const handleHanziGradeChange = async (hanziChar, e) => {
        e.stopPropagation();
        const newGrade = parseInt(e.target.value, 10);
        const userId = getUserId();

        if (!userId) {
            logger.error("ChineseLookup - No user ID available, cannot update hanzi grade");
            setError("No user ID available. Please log in again.");
            return;
        }

        // Update UI immediately for better user experience
        setHanziGrades(prev => ({ ...prev, [hanziChar]: newGrade }));

        try {
            logger.debug(`ChineseLookup - Updating grade for Hanzi "${hanziChar}" to ${newGrade}`);
            // Make direct API call to the server
            const result = await apiClient.post('/api/updateHanziGrade', {
                userId,
                hanzi: hanziChar,
                grade: newGrade
            });

            // Check if result is valid
            if (!result || !result.message) {
                logger.error("ChineseLookup - Failed to update hanzi grade: Invalid response");
                throw new Error("Failed to update hanzi grade: Invalid response");
            }

            logger.debug("ChineseLookup - Hanzi grade updated successfully:", result.message);

            // Force refresh saved words to update context state
            refreshSavedWords();
        } catch (error) {
            logger.error(`ChineseLookup - Error updating grade for Hanzi ${hanziChar}:`, error);
            setError("Failed to update Hanzi grade.");
        }
    };

    const handleHanziKnownChange = async (hanziChar, e) => {
        e.stopPropagation();
        const isKnown = e.target.checked;
        const userId = getUserId();

        if (!userId) {
            logger.error("ChineseLookup - No user ID available, cannot update hanzi known status");
            setError("No user ID available. Please log in again.");
            return;
        }

        // Update UI immediately for better user experience
        setHanziKnownStatus(prev => ({ ...prev, [hanziChar]: isKnown }));

        try {
            logger.debug(`ChineseLookup - Updating known status for Hanzi "${hanziChar}" to ${isKnown}`);
            // Make direct API call to the server
            const result = await apiClient.post('/api/updateHanziKnown', {
                userId,
                hanzi: hanziChar,
                known: isKnown
            });

            // Check if result is valid
            if (!result || !result.message) {
                logger.error("ChineseLookup - Failed to update hanzi known status: Invalid response");
                throw new Error("Failed to update hanzi known status: Invalid response");
            }

            logger.debug("ChineseLookup - Hanzi known status updated successfully:", result.message);

            // Force refresh saved words to update context state
            refreshSavedWords();
        } catch (error) {
            logger.error(`ChineseLookup - Error updating known status for Hanzi ${hanziChar}:`, error);
            setError("Failed to update Hanzi status.");
        }
    };

    // --- UI Rendering Functions ---

    // Word Grading UI (only if saved)
    const renderWordGradingUI = () => {
        if (!isSaved) return null;
        return (
            <div className="stories-kanji-grading" onClick={(e) => e.stopPropagation()}>
                {/* Difficulty Scale */}
                <div className="difficulty-scale-stories">
                    <span className="stories-difficulty-hard">Hard</span>
                    <span className="stories-difficulty-easy">Easy</span>
                </div>
                {/* Radio Buttons */}
                <div className="stories-radio-group">
                    {[1, 2, 3, 4, 5].map(grade => (
                        <div key={grade} className={`stories-radio-option stories-radio-grade-${grade} ${wordGrade === grade ? 'selected' : ''}`}>
                            <input type="radio" id={`grade-word-${grade}`} name="word-grade" value={grade} checked={wordGrade === grade} onChange={handleGradeChange} />
                            <label htmlFor={`grade-word-${grade}`} onClick={(e) => e.stopPropagation()}>{grade}</label>
                        </div>
                    ))}
                </div>
                {/* Known Checkbox */}
                <div className="stories-known-checkbox">
                    <input type="checkbox" id="word-known" checked={wordKnown} onChange={handleKnownChange} />
                    <div>
                        <label htmlFor="word-known" className="stories-known-checkbox-label">I know this word now.</label>
                        <div className="stories-known-checkbox-description">Check this box to remove the highlight.</div>
                    </div>
                </div>
            </div>
        );
    };

    // Hanzi Grading UI (only if saved) - corrected function calls
    const renderHanziGradingUI = (hanziChar) => {
        const isCharSaved = isHanziSaved(hanziChar) || justSavedHanzi[hanziChar]; // Check in object instead of comparing
        if (!isCharSaved) return null;
        return (
            <div className="stories-kanji-grading" onClick={(e) => e.stopPropagation()}>
                {/* Difficulty Scale */}
                <div className="difficulty-scale-stories">
                    <span className="stories-difficulty-hard">Hard</span>
                    <span className="stories-difficulty-easy">Easy</span>
                </div>
                {/* Radio Buttons */}
                <div className="stories-radio-group">
                    {[1, 2, 3, 4, 5].map(grade => (
                        <div key={grade} className={`stories-radio-option stories-radio-grade-${grade} ${hanziGrades[hanziChar] === grade ? 'selected' : ''}`}>
                            <input type="radio" id={`grade-${hanziChar}-${grade}`} name={`hanzi-grade-${hanziChar}`} value={grade} checked={hanziGrades[hanziChar] === grade} onChange={(e) => handleHanziGradeChange(hanziChar, e)} />
                            <label htmlFor={`grade-${hanziChar}-${grade}`} onClick={(e) => e.stopPropagation()}>{grade}</label>
                        </div>
                    ))}
                </div>
                {/* Known Checkbox */}
                <div className="stories-known-checkbox">
                    <input type="checkbox" id={`known-${hanziChar}`} checked={hanziKnownStatus[hanziChar] || false} onChange={(e) => handleHanziKnownChange(hanziChar, e)} />
                    <div>
                        <label htmlFor={`known-${hanziChar}`} className="stories-known-checkbox-label">I know this Hanzi now.</label>
                        <div className="stories-known-checkbox-description">Check this box to remove the highlight.</div>
                    </div>
                </div>
            </div>
        );
    };

    // Render individual Hanzi character details - Updated to match Kanji display format
    const renderHanziInfo = (hanziItem) => {
        // hanziItem contains { literal: '字', definition: { /* details from hanzi_db_fixed.json */ } }
        const details = hanziItem.definition; // The details object fetched from the backend

        return (
            <div className="kanji-info" key={hanziItem.literal}> {/* Reuse styling */}
                {/* Display fields based on hanzi_db_fixed.json structure, matching Kanji order */}
                {details ? (
                    <div>
                        {/* Meaning */}
                        {details.definition && (
                            <p>
                                <span className="kanji-info-header">Meaning:</span>
                                <span className="kanji-info-text">{details.definition}</span>
                            </p>
                        )}
                        {/* Reading (Pinyin) */}
                        {details.pinyin && (
                            <p>
                                <span className="kanji-info-header">Reading:</span>
                                <span className="kanji-info-text">{details.pinyin}</span>
                            </p>
                        )}
                        {/* Grade (HSK Level) */}
                        {details.hsk_level && (
                            <p>
                                <span className="kanji-info-header">Grade:</span>
                                <span className="kanji-info-text">{details.hsk_level}</span>
                            </p>
                        )}
                        {/* Frequency Rank */}
                        {details.frequency_rank && (
                            <p>
                                <span className="kanji-info-header">Frequency:</span>
                                <span className="kanji-info-text">{details.frequency_rank}</span>
                            </p>
                        )}
                        {/* Optional: Add other fields if needed, like stroke_count, radical */}
                        {/* {details.stroke_count && <p><span className="kanji-info-header">Strokes:</span> <span className="kanji-info-text">{details.stroke_count}</span></p>} */}
                        {/* {details.radical && <p><span className="kanji-info-header">Radical:</span> <span className="kanji-info-text">{details.radical}</span></p>} */}
                    </div>
                ) : (
                    <p>Details not found for this character.</p>
                )}

                <hr className="definition-separator" />
                {/* Save/Delete Buttons */}
                <div className="stories-WordLookup-popup-buttons" onClick={(e) => e.stopPropagation()}>
                    {!(isHanziSaved(hanziItem.literal) || justSavedHanzi[hanziItem.literal]) ? (
                        <button className="save-word-btn" onClick={(e) => { e.stopPropagation(); handleSaveHanzi(hanziItem.literal); }}>Save Hanzi</button>
                    ) : (
                        <button className="delete-word-btn" onClick={(e) => { e.stopPropagation(); handleDeleteHanzi(hanziItem.literal); }}>Delete Hanzi</button>
                    )}
                </div>
                {/* Grading UI */}
                {renderHanziGradingUI(hanziItem.literal)}
            </div>
        );
    };

    const handlePlayWord = async (wordToPlay) => {
        if (isPlaying) return;
        const wordForTTS = wordToPlay || word;
        
        // Check cache first
        if (cachedAudioUrl.current) {
            try {
                setIsPlaying(true);
                const audio = new Audio(cachedAudioUrl.current);
                audio.crossOrigin = 'anonymous';
                audio.onended = () => setIsPlaying(false);
                audio.onerror = () => {
                    cachedAudioUrl.current = null;
                    setIsPlaying(false);
                };
                await audio.play();
                return;
            } catch (e) {
                cachedAudioUrl.current = null;
                setIsPlaying(false);
            }
        }

        try {
            setIsPlaying(true);
            const payload = {
                text: wordForTTS,
                learningLanguage: 'Chinese',
            };
            const data = await apiClient.post('/api/tts', payload);
            const url = data.audio;
            cachedAudioUrl.current = url;
            const audio = new Audio(url);
            audio.crossOrigin = 'anonymous';
            audio.onended = () => setIsPlaying(false);
            audio.onerror = () => {
                cachedAudioUrl.current = null;
                setIsPlaying(false);
            };
            await audio.play();
        } catch (error) {
            logger.error('ChineseLookup - Error playing TTS:', error);
            cachedAudioUrl.current = null;
            setIsPlaying(false);
        }
    };

    // Main Popup Content Structure - Updated to pass correct data to renderHanziInfo
    const renderPopupContent = () => (
        <div onClick={(e) => e.stopPropagation()}>
            <div className="kanji-definition-container"> {/* Reuse class */}
                <div className="stories-word-heading-row">
                    <strong>{word}</strong>
                    <button
                        className="stories-speaker-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            handlePlayWord(word);
                        }}
                        disabled={isPlaying}
                        title="Play pronunciation"
                    >
                        <SpeakerIcon className="stories-speaker-icon" />
                    </button>
                </div>
                <span style={{ marginLeft: '10px', fontStyle: 'italic' }}>{data?.pinyin || ''}</span>
                <hr className="definition-separator" />
                <div className="stories-definition-content">
                    {data?.definitions?.map((def, i) => <p key={i}>{def}</p>) || <p>Loading definition...</p>}
                </div>

                {/* Context Accordion */}
                {sentence && ( // Only show if a sentence context is provided
                    <div className="JP-stories-definition-accordion">
                        <div
                            className="JP-stories-definition-accordion-header"
                            onClick={(e) => toggleTopAccordion('context', e)}
                        >
                            <h4 className="JP-stories-definition-accordion-title">Context</h4>
                            <span className="JP-stories-definition-accordion-icon">
                                {openAccordion === 'context' ? '▼' : '▶'}
                            </span>
                        </div>
                        {openAccordion === 'context' && (
                            <div className="JP-stories-definition-accordion-content">
                                {contextLoading && <p>Loading context...</p>}
                                {contextError && <p className="stories-error-text">Error: {contextError}</p>}
                                {!contextLoading && !contextError && contextData?.context_note && (
                                    <p className="context-note">{contextData.context_note}</p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Grammar Accordion */}
                {sentence && ( // Only show if a sentence context is provided
                    <div className="JP-stories-definition-accordion">
                        <div
                            className="JP-stories-definition-accordion-header"
                            onClick={(e) => toggleTopAccordion('grammar', e)}
                        >
                            <h4 className="JP-stories-definition-accordion-title">Grammar</h4>
                            <span className="JP-stories-definition-accordion-icon">
                                {openAccordion === 'grammar' ? '▼' : '▶'}
                            </span>
                        </div>
                        {openAccordion === 'grammar' && (
                            <div className="JP-stories-definition-accordion-content">
                                {grammarLoading && <p>Loading grammar...</p>}
                                {grammarError && <p className="stories-error-text">Error: {grammarError}</p>}
                                {!grammarLoading && !grammarError && grammarData?.grammar_note && (
                                    <p className="grammar-note">{grammarData.grammar_note}</p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Example Sentences Accordion */}
                {sentence && contextData && contextData.examples && Array.isArray(contextData.examples) && contextData.examples.length > 0 && (
                    <div className="JP-stories-definition-accordion">
                        <div
                            className="JP-stories-definition-accordion-header"
                            onClick={(e) => toggleTopAccordion('examples', e)}
                        >
                            <h4 className="JP-stories-definition-accordion-title">Example Sentences</h4>
                            <span className="JP-stories-definition-accordion-icon">
                                {openAccordion === 'examples' ? '▼' : '▶'}
                            </span>
                        </div>
                        {openAccordion === 'examples' && (
                            <div className="JP-stories-definition-accordion-content">
                                {contextData.examples.map((example, index) => (
                                    <div key={index} style={{ marginBottom: '12px' }}>
                                        <p style={{
                                            color: '#ffffffff',
                                            fontWeight: '500',
                                            marginBottom: '4px',
                                            fontSize: '15px'
                                        }}>
                                            {example.sentence}
                                        </p>
                                        <p style={{
                                            color: '#ddddddff',
                                            fontStyle: 'italic',
                                            marginTop: '0',
                                            fontSize: '14px'
                                        }}>
                                            {example.translation}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {loading && !data && <p>Loading...</p>} {/* Show loading only if no data yet */}

                {/* Save/Delete Word Buttons */}
                <div className="stories-WordLookup-popup-buttons" onClick={(e) => e.stopPropagation()}>
                    {!isSaved ? (
                        <button className="save-word-btn" onClick={handleSaveWord}>Save Word</button>
                    ) : (
                        <button className="delete-word-btn" onClick={handleDeleteWord}>Delete Word</button>
                    )}
                </div>

                {/* Word Grading */}
                {renderWordGradingUI()}

                {error && <div className="stories-error-text">{error}</div>}
            </div>

            {/* Hanzi Components Section */}
            {hanziChars.length > 0 && (
                <>
                    <hr className="definition-separator" />
                    <div className="kanji-definition-container"> {/* Reuse class */}
                        <h4 className="kanji-components-title">Hanzi Components:</h4>
                        {/* Show loading indicator while hanzi details are fetched */}
                        {loading && !hanziDetailsData && <p>Loading Hanzi details...</p>}

                        {/* Render only when hanziDetailsData is loaded */}
                        {hanziDetailsData && hanziChars.map((char, index) => (
                            <div key={index} className="stories-kanji-accordion">
                                <div
                                    className="stories-kanji-accordion-header"
                                    onClick={(e) => { e.stopPropagation(); setOpenAccordionIndex(index === openAccordionIndex ? null : index); }}
                                >
                                    <span className="kanji-accordion-header-char">{char}</span>
                                    <span className="stories-kanji-accordion-icon">{openAccordionIndex === index ? '▼' : '▶'}</span>
                                </div>
                                {openAccordionIndex === index && (
                                    <div className="stories-kanji-accordion-body" onClick={(e) => e.stopPropagation()}>
                                        {/* Pass the character and its fetched details */}
                                        {renderHanziInfo({ literal: char, definition: hanziDetailsData[char] })}
                                    </div>
                                )}
                            </div>
                        ))}
                        {/* Handle case where loading finished but no details found (optional) */}
                        {!loading && hanziDetailsData && Object.keys(hanziDetailsData).length === 0 && hanziChars.length > 0 && <p>Details not available.</p>}
                    </div>
                </>
            )}
        </div>
    );

    // --- Main Component Return ---

    // Determine CSS class for word based on saved/known status
    const getWordClass = () => {
        if (!isSaved) return 'clickable-word chinese-word'; // Base class
        if (wordKnown) return 'clickable-word chinese-word'; // Known, no highlight
        return `clickable-word chinese-word saved-word grade-${wordGrade}`; // Saved + graded
    };

    // Determine CSS class for individual Hanzi characters - corrected function calls
    const getHanziClass = (hanziChar) => {
        const isCharSaved = isHanziSaved(hanziChar) || justSavedHanzi[hanziChar]; // Check in object instead of comparing
        if (!isCharSaved) return '';
        if (hanziKnownStatus[hanziChar]) return ''; // Known, no highlight
        return `saved-word grade-${hanziGrades[hanziChar] || 1}`;
    };

    return (
        <span
            ref={spanRef}
            className={getWordClass()}
            onClick={handleClick}
            style={{ margin: 0, padding: 0 }}
        >
            {/* Render children, applying Hanzi highlight if applicable - no spaces between characters */
                React.Children.map(children, child => {
                    if (typeof child === 'string') {
                        return Array.from(child).map((char, idx) => {
                            const isHanziChar = /[\u4E00-\u9FFF]/.test(char);
                            const hanziClass = isHanziChar ? getHanziClass(char) : '';
                            // Only apply spacing properties
                            return <span key={idx} className={hanziClass} style={{ margin: 0, padding: 0, display: 'inline-block' }}>{char}</span>;
                        });
                    }
                    return child;
                })
            }

            {/* Popup Portal */}
            {showPopup && ReactDOM.createPortal(
                <div
                    ref={popupRef}
                    className="stories-definition-popup" // Reuse class
                    style={{
                        position: 'absolute',
                        top: popupPosition.top,
                        left: popupPosition.left,
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {renderPopupContent()}
                </div>,
                document.body
            )}
        </span>
    );
};

export default ChineseLookup;